import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { OtpConfigService } from "../services/otp-config.service";
import { isDeepEqual } from "../utils/deep-equal";
import type {
  GetOtpConfigResponse,
  OtpChannel,
  OtpStrategy,
  OtpUiTexts,
} from "@codway-dev/shared";

// ---------- Types ----------

/**
 * The subset of editable config fields — shared by the live editor state
 * and by the dirty-check snapshot, so the two can never drift apart.
 */
interface OtpConfigFields {
  isEnabled: boolean;
  channel: OtpChannel;
  strategy: OtpStrategy;
  minOrderValueForOtp: number | undefined;
  validityPeriodHours: number;
  maxResends: number;
  maxVerifyAttempts: number;
  smsTemplate: string;
  blockVoipNumbers: boolean;
  requireCountryMatch: boolean;
  uiTexts: OtpUiTexts;
}

interface OtpConfigEditorState extends OtpConfigFields {
  initialSnapshot: OtpConfigSnapshot | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface OtpConfigEditorActions {
  loadFromBackend: () => Promise<void>;
  updateField: <K extends keyof OtpConfigEditorState>(
    key: K,
    value: OtpConfigEditorState[K]
  ) => void;
  updateUiText: (key: keyof OtpUiTexts, value: string) => void;
  save: () => Promise<void>;
  discardChanges: () => void;
}

type OtpConfigEditorStore = OtpConfigEditorState & OtpConfigEditorActions;

// ---------- Snapshot ----------

type OtpConfigSnapshot = OtpConfigFields;

function getSnapshot(state: OtpConfigEditorState): OtpConfigSnapshot {
  return {
    isEnabled: state.isEnabled,
    channel: state.channel,
    strategy: state.strategy,
    minOrderValueForOtp: state.minOrderValueForOtp,
    validityPeriodHours: state.validityPeriodHours,
    maxResends: state.maxResends,
    maxVerifyAttempts: state.maxVerifyAttempts,
    smsTemplate: state.smsTemplate,
    blockVoipNumbers: state.blockVoipNumbers,
    requireCountryMatch: state.requireCountryMatch,
    uiTexts: { ...state.uiTexts },
  };
}

function applyConfigToState(
  state: OtpConfigEditorState,
  data: GetOtpConfigResponse
) {
  state.isEnabled = data.is_enabled;
  state.channel = data.channel;
  state.strategy = data.strategy;
  state.minOrderValueForOtp = data.min_order_value_for_otp;
  state.validityPeriodHours = data.validity_period_hours;
  state.maxResends = data.max_resends;
  state.maxVerifyAttempts = data.max_verify_attempts;
  state.smsTemplate = data.sms_template;
  state.blockVoipNumbers = data.security_rules.block_voip_numbers;
  state.requireCountryMatch = data.security_rules.require_country_match;
  state.uiTexts = data.ui_texts;
}

// ---------- Selector ----------

export const selectOtpConfigIsDirty = (state: OtpConfigEditorStore) => {
  if (!state.initialSnapshot) return false;
  return !isDeepEqual(getSnapshot(state), state.initialSnapshot);
};

// ---------- Store ----------

export const useOtpConfigStore = create<OtpConfigEditorStore>()(
  devtools(
    immer((set, get) => ({
      isEnabled: false,
      channel: "whatsapp",
      strategy: "disabled",
      minOrderValueForOtp: undefined,
      validityPeriodHours: 24,
      maxResends: 3,
      maxVerifyAttempts: 5,
      smsTemplate: "Your verification code is {otp_code}.",
      blockVoipNumbers: true,
      requireCountryMatch: true,
      uiTexts: {
        title: "Verify your phone number to complete your order",
        description:
          "A verification code was sent via SMS/WhatsApp to your phone number {phone_number}. Enter your code below to verify your phone number:",
        verify_button: "Verify",
        resend_button: "Resend code",
        change_phone_button: "Change phone number",
        hint_text: "Didn't receive your code?",
        new_code_sent_text:
          "A new code was sent via SMS to your phone number {phone_number}. Enter the code below:",
        invalid_code_error:
          "The code you entered is not valid, please try again.",
        resends_limit_error:
          "You have reached the limit of resends, please try again with a different phone number.",
        contact_support_button: "Contact Support",
        support_message_template:
          "Hi, I'm trying to complete my order but I haven't received my verification code. Can you help me?",
      },

      initialSnapshot: null,
      isLoading: false,
      isSaving: false,
      error: null,

      loadFromBackend: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const data = await OtpConfigService.getConfig();
          set((state) => {
            applyConfigToState(state, data);
            state.initialSnapshot = getSnapshot(state);
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : "Failed to load settings";
            state.isLoading = false;
          });
        }
      },

      updateField: (key, value) =>
        set((state) => {
          state[key] = value as never;
        }),

      updateUiText: (key, value) =>
        set((state) => {
          state.uiTexts[key] = value;
        }),

      save: async () => {
        set((state) => {
          state.isSaving = true;
        });
        try {
          const s = get();
          const data = await OtpConfigService.updateConfig({
            is_enabled: s.isEnabled,
            channel: s.channel,
            strategy: s.strategy,
            min_order_value_for_otp: s.minOrderValueForOtp,
            validity_period_hours: s.validityPeriodHours,
            max_resends: s.maxResends,
            max_verify_attempts: s.maxVerifyAttempts,
            sms_template: s.smsTemplate,
            security_rules: {
              block_voip_numbers: s.blockVoipNumbers,
              require_country_match: s.requireCountryMatch,
            },
            ui_texts: s.uiTexts,
          });

          set((state) => {
            applyConfigToState(state, data);
            state.initialSnapshot = getSnapshot(state);
            state.isSaving = false;
          });
        } catch {
          set((state) => {
            state.isSaving = false;
          });
          throw new Error("Error al guardar la configuración de OTP");
        }
      },

      discardChanges: () =>
        set((state) => {
          if (state.initialSnapshot) {
            Object.assign(state, state.initialSnapshot, {
              uiTexts: { ...state.initialSnapshot.uiTexts },
            });
          }
        }),
    })),
    { name: "OtpConfigEditorStore" }
  )
);
